import { Item, ItemArtist, ItemCategory, ItemCollection, ItemCondition, ItemGenre, ItemPartner, ItemSubCategory } from '@/api';
import { getAllCollectionsQueryFn } from '@/queries/collection';
import { getAllCategoriesQueryFn, getAllArtistQueryFn, getAllConditionsQueryFn, getAllGenresQueryFn, getAllPartnersQueryFn } from '@/queries/meta';
import { MetaAvailableResponse } from '@/queries/search';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import sortBy from 'lodash.sortby';
import { useTranslation } from 'react-i18next';

export const useMeta = (availableMeta?: MetaAvailableResponse) => {
    const { i18n: i18next } = useTranslation();

    const { data: categories, isFetching: isFetchingCategories } = useQuery({
        queryKey: ['getAllCategoriesQueryFn'],
        queryFn: getAllCategoriesQueryFn,
        staleTime: 1000 * 60 * 5
    });

    const { data: artists, isFetching: isFetchingArtists } = useQuery({
        queryKey: ['getAllArtistQueryFn'],
        queryFn: getAllArtistQueryFn,
        staleTime: 1000 * 60 * 5
    });

    const { data: conditions, isFetching: isFetchingConditions } = useQuery({
        queryKey: ['getAllConditionsQueryFn'],
        queryFn: getAllConditionsQueryFn,
        staleTime: 1000 * 60 * 5
    });

    const { data: partners, isFetching: isFetchingPartnerss } = useQuery({
        queryKey: ['getAllPartnersQueryFn'],
        queryFn: getAllPartnersQueryFn,
        staleTime: 1000 * 60 * 5
    });

    const { data: genres, isFetching: isFetchingGenres } = useQuery({
        queryKey: ['getAllGenresQueryFn'],
        queryFn: getAllGenresQueryFn,
        staleTime: 1000 * 60 * 5
    });

    const { data: collections, isFetching: isFetchingCollections } = useQuery({
        queryKey: ['getAllCollectionsQueryFn'],
        queryFn: getAllCollectionsQueryFn,
        staleTime: 1000 * 60 * 5
    });

    const subCategories = useMemo(() => {
        return categories?.reduce((prev, category) => {
            return [...prev, ...category.sub_categories];
        }, [] as Array<ItemSubCategory>);
    }, [categories]);

    const findAvailable = useCallback((type: keyof MetaAvailableResponse, id: string) => {
        if (!availableMeta) {
            return undefined;
        }
        return availableMeta[type].find((a) => a.id === id)?.available || -1;
    }, [availableMeta]);

    const actualCategories = useMemo(() => {
        return sortBy(categories?.map((e) => {
            return { ...e, available: findAvailable('category', e.id) } as ItemCategory & { available?: number };
        }).filter((e) => e.available !== -1), 'name');
    }, [categories, findAvailable]);

    const actualGenres = useMemo(() => {
        return sortBy(genres?.map((e) => {
            return { ...e, available: findAvailable('genres', e.id) } as ItemGenre & { available?: number };
        }).filter((e) => e.available !== -1), 'name');
    }, [genres, findAvailable]);

    const actualConditions = useMemo(() => {
        return sortBy(conditions?.map((e) => {
            return { ...e, available: findAvailable('conditions', e.id) } as ItemCondition & { available?: number };
        }).filter((e) => e.available !== -1), 'name');
    }, [conditions, findAvailable]);

    const actualPartnerss = useMemo(() => {
        return sortBy(partners?.map((e) => {
            return { ...e, available: findAvailable('partners', e.id) } as ItemPartner & { available?: number };
        }).filter((e) => e.available !== -1), 'name');
    }, [partners, findAvailable]);

    const actualArtists = useMemo(() => {
        return sortBy(artists?.map((e) => {
            return { ...e, available: findAvailable('artists', e.id) } as ItemArtist & { available?: number };
        }).filter((e) => e.available !== -1), 'name');
    }, [artists, findAvailable]);

    const actualSubCategories = useMemo(() => {
        return sortBy(subCategories?.map((e) => {
            return { ...e, available: findAvailable('sub_category', e.id) } as ItemSubCategory & { available?: number };
        }).filter((e) => e.available !== -1), 'name');
    }, [subCategories, findAvailable]);

    const actualCollections = useMemo(() => {
        return sortBy(collections?.map((e) => {
            return { ...e, available: findAvailable('collections', e.id) } as ItemCollection & { available?: number };
        }).filter((e) => e.available !== -1), 'name');
    }, [collections, findAvailable]);

    const actualOfferTypes = useMemo(() => {
        return Object.values(Item.OfferTypeEnum).filter((type) => type !== Item.OfferTypeEnum.OfferInstant)?.map((e) => {
            const own = findAvailable('offer_types', `${e}`);
            let available = own;
            /*
             * Buy Now stands for instant offers too — searchQueryFn sends
             * offer_instant alongside buy_now — so the two counts add up.
             *
             * They are added only where they are numbers. `-1` is
             * findAvailable's "not in the facet" sentinel, and the old sum
             * treated it as one: with neither present it produced -2, which
             * slipped past the `!== -1` below and left Buy Now selectable on
             * a collection that has none of it — pick it and the grid empties.
             * With one present it quietly knocked 1 off the count.
             *
             * `own === undefined` means there is no facet at all yet, and
             * every type stays available until one arrives.
             */
            if (e === Item.OfferTypeEnum.BuyNow && own !== undefined) {
                const instant = findAvailable('offer_types', `${Item.OfferTypeEnum.OfferInstant}`);
                const counted = [own, instant].filter((n): n is number => typeof n === 'number' && n !== -1);
                available = counted.length ? counted.reduce((sum, n) => sum + n, 0) : -1;
            }
            return {
                value: e,
                available,
                label: i18next.t(`offer-types.${e}` as 'offer-types.auction') as string
            } as { value: string, label: string } & { available?: number };
        }).filter((e) => e.available !== -1);
    }, [findAvailable, i18next]);

    return {
        categories: actualCategories,
        artists: actualArtists,
        conditions: actualConditions,
        partners: actualPartnerss,
        genres: actualGenres,
        subCategories: actualSubCategories,
        collections: actualCollections,
        offer_types: actualOfferTypes,
        isFetching: isFetchingArtists || isFetchingCategories || isFetchingConditions || isFetchingPartnerss || isFetchingGenres || isFetchingCollections
    };
};
